import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
  formatOdds,
  americanToDecimal,
  decimalToAmerican,
  getOddsFormat,
  setOddsFormat as saveOddsFormat
} from '../utils/oddsUtils';

function PositiveEVCalculator() {
  const location = useLocation();
  const passedData = location.state;

  const [oddsFormat, setOddsFormat] = useState(getOddsFormat());
  const [bankroll, setBankroll] = useState(10000);
  const [kellyMultiplier, setKellyMultiplier] = useState(0.25);
  const [odds, setOdds] = useState('');
  const [winProbability, setWinProbability] = useState(50);
  const [showDescription, setShowDescription] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  const handleOddsFormatChange = (newFormat) => {
    // Convert current odds to decimal first
    const decimalOddsValue = oddsFormat === 'American' ? americanToDecimal(odds) : parseFloat(odds) || 2.0;

    // Convert to new format
    if (newFormat === 'American') {
      setOdds(decimalToAmerican(decimalOddsValue));
    } else {
      setOdds(decimalOddsValue.toFixed(2));
    }

    setOddsFormat(newFormat);
    saveOddsFormat(newFormat);
  };

  useEffect(() => {
    if (passedData) {
      // Data comes in as decimal odds and probability as decimal (0-1)
      const format = getOddsFormat();
      setBankroll(passedData.bankroll || 10000);
      setKellyMultiplier(passedData.kellyMultiplier || 0.25);
      setOdds(formatOdds(passedData.odds || 2.0, format));
      setWinProbability(Math.round((passedData.trueProbability || 0.5) * 10000) / 100);
    } else {
      // Set defaults based on format
      const format = getOddsFormat();
      setOdds(format === 'American' ? '+110' : '2.10');
    }
  }, [passedData]);

  // Convert display odds to decimal for calculations
  const getDecimalOdds = () => {
    if (oddsFormat === 'American') {
      return americanToDecimal(odds);
    }
    return parseFloat(odds) || 2.0;
  };

  const decimalOdds = getDecimalOdds();
  const winProb = Number(winProbability) / 100;
  const expectedValue = ((winProb * decimalOdds) - 1) * 100;

  const b = decimalOdds - 1;
  const q = 1 - winProb;
  const kellyFraction = ((b * winProb) - q) / b;
  const fractionOfBankroll = kellyFraction * 100;

  const amountToWager = Number(bankroll) * Number(kellyMultiplier) * kellyFraction;

  const getBorderColor = (value) => {
    if (value > -0.01 && value < 0.01) return '#4a5568';
    return value > 0 ? '#10b981' : '#fc8181';
  };

  return (
    <div style={{
      backgroundColor: '#0a0e1a',
      minHeight: '100vh',
      padding: '40px 60px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            margin: 0,
            color: '#ffffff',
            letterSpacing: '-0.5px'
          }}>
            Positive EV Calculator
          </h1>

          {/* Odds Format Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'American' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              American
            </span>
            <div
              onClick={() => handleOddsFormatChange(oddsFormat === 'American' ? 'Decimal' : 'American')}
              style={{
                width: '48px',
                height: '26px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #2d3748',
                borderRadius: '13px',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: oddsFormat === 'American' ? '3px' : '23px',
                transition: 'left 0.2s ease'
              }} />
            </div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'Decimal' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              Decimal
            </span>
          </div>
        </div>

        {/* Description */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '10px'
        }}>
          <div
            onClick={() => setShowDescription(!showDescription)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              Description
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showDescription ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showDescription && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#a0aec0',
              margin: '15px 0 0 0'
            }}>
              <p style={{ margin: '0 0 10px 0' }}>
                Positive EV bets have odds that exceed the true probability of winning, creating long-term profit.
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                Use this calculator to test opportunities, experiment with bankroll sizes and Kelly multipliers, or verify EV calculations before placing bets.
              </p>
              <p style={{ margin: 0 }}>
                A lower Kelly multiplier (like 0.25) is more conservative and reduces variance.
              </p>
            </div>
          )}
        </div>

        {/* How to Use */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '20px'
        }}>
          <div
            onClick={() => setShowHowTo(!showHowTo)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              How to Use
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showHowTo ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showHowTo && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#a0aec0',
              margin: '15px 0 0 0'
            }}>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>Enter your bankroll and Kelly multiplier (0.25 is conservative)</li>
                <li style={{ marginBottom: '8px' }}>Input the odds offered by the sportsbook</li>
                <li style={{ marginBottom: '8px' }}>Set the true win probability (from sharp books or your own model)</li>
                <li>Positive EV and a recommended wager appear when there's an edge</li>
              </ol>
            </div>
          )}
        </div>

        {/* Calculator */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '30px'
        }}>
          {/* Inputs */}
          <div style={{
            backgroundColor: '#0f1419',
            padding: '24px',
            borderRadius: '10px',
            marginBottom: '24px',
            border: '1px solid #2d3748'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Bankroll ($)
                </label>
                <input
                  type="number"
                  step="1000"
                  value={bankroll}
                  onChange={(e) => setBankroll(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    backgroundColor: '#1a1f2e',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Kelly Multiplier
                </label>
                <input
                  type="number"
                  step="0.05"
                  value={kellyMultiplier}
                  onChange={(e) => setKellyMultiplier(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    backgroundColor: '#1a1f2e',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Odds ({oddsFormat})
                </label>
                <input
                  type="text"
                  value={odds}
                  onChange={(e) => setOdds(e.target.value)}
                  placeholder={oddsFormat === 'American' ? '+110' : '2.10'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    backgroundColor: '#1a1f2e',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Win % (True Probability)
                </label>
                <input
                  type="number"
                  step="1"
                  value={winProbability}
                  onChange={(e) => setWinProbability(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    backgroundColor: '#1a1f2e',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div style={{
            backgroundColor: '#0f1419',
            padding: '24px',
            borderRadius: '10px',
            border: `3px solid ${getBorderColor(expectedValue)}`,
            color: '#ffffff'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '12px',
              fontSize: '14px'
            }}>
              <span style={{ color: '#a0aec0' }}>Expected Value</span>
              <span style={{
                fontWeight: '700',
                fontSize: '18px',
                fontFamily: 'monospace',
                color: expectedValue > 0 ? '#10b981' : expectedValue < -0.01 ? '#f87171' : '#ffffff'
              }}>
                {expectedValue > 0 ? '+' : ''}{expectedValue.toFixed(2)}%
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '12px',
              fontSize: '14px'
            }}>
              <span style={{ color: '#a0aec0' }}>Fraction of Bankroll</span>
              <span style={{
                fontWeight: '700',
                fontSize: '18px',
                fontFamily: 'monospace',
                color: expectedValue > 0 ? '#10b981' : expectedValue < -0.01 ? '#f87171' : '#ffffff'
              }}>
                {Math.max(0, fractionOfBankroll).toFixed(2)}%
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '14px'
            }}>
              <span style={{ color: '#a0aec0' }}>Amount to Wager</span>
              <span style={{
                fontWeight: '700',
                fontSize: '20px',
                fontFamily: 'monospace',
                color: expectedValue > 0 ? '#10b981' : expectedValue < -0.01 ? '#f87171' : '#ffffff'
              }}>
                ${Math.max(0, amountToWager).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PositiveEVCalculator;
